import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AssignmentUnit } from './AssignmentUnit';
import { ClassUnit } from './ClassUnit';
import { api, type CourseDetail, type UnitDetail } from '../api';
import { CraftLesson } from '../craft/Blocks';
import { TestPlayer } from './TestPlayer';

export function UnitPage() {
  const { courseId, unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState<UnitDetail | null>(null);
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [completionNotice, setCompletionNotice] = useState<string | null>(null);
  const [completionChoice, setCompletionChoice] = useState<number[]>([]);
  const openedAt = useRef(Date.now());

  const load = useCallback(async () => {
    setUnit(null); setError(null); setCompletionNotice(null); setCompletionChoice([]);
    openedAt.current = Date.now();
    try {
      const [u, c] = await Promise.all([
        api.get<UnitDetail>(`/courses/${courseId}/units/${unitId}`),
        api.get<CourseDetail>(`/courses/${courseId}`),
      ]);
      setUnit(u); setCourse(c);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [courseId, unitId]);

  /**
   * After a test is submitted, refresh the course so the unit list and the
   * progress bar catch up — but leave this page mounted, so the learner keeps
   * looking at their result.
   */
  const refreshCourse = useCallback(async () => {
    try { setCourse(await api.get<CourseDetail>(`/courses/${courseId}`)); } catch { /* keep the result on screen */ }
  }, [courseId]);

  useEffect(() => { void load(); }, [load]);

  // Keep time-on-unit honest even if the learner never presses the button.
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const seconds = document.visibilityState === 'visible' ? Math.min(30, Math.floor((now - openedAt.current) / 1000)) : 0;
      openedAt.current = now;
      if (unit) void api.post(`/courses/${courseId}/units/${unitId}/heartbeat`, { seconds }).catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [courseId, unitId, unit]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!unit || !course) return <p className="muted">Loading…</p>;

  const list = course.units.filter((u) => u.type !== 'section');
  const at = list.findIndex((u) => u.id === unit.id);
  const next = list.slice(at + 1).find((u) => !u.locked);
  const done = unit.progress?.status === 'completed';
  const questionCompletionUnavailable = unit.completionType === 'question' && !unit.completionParams?.configured;

  const finish = async () => {
    setSaving(true);
    try {
      const seconds = Math.min(3600, Math.round((Date.now() - openedAt.current) / 1000));
      const out = await api.post<{ status: string; secondsRemaining?: number }>(`/courses/${courseId}/units/${unitId}/complete`, { seconds, data: { choice: completionChoice } });
      openedAt.current = Date.now();
      if (out.status !== 'completed') { setCompletionNotice(`Keep studying for ${out.secondsRemaining ?? 0} more seconds before completing this lesson.`); return; }
      if (next) navigate(`/courses/${courseId}/units/${next.id}`);
      else navigate(`/courses/${courseId}`);
    } catch (e) {
      setCompletionNotice((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="unit">
      <nav className="crumbs">
        <Link to="/">My courses</Link> <span aria-hidden>›</span>{' '}
        <Link to={`/courses/${courseId}`}>{course.course.name}</Link>
      </nav>
      <h1 className="h1">{unit.name}</h1>

      <UnitBody
        unit={unit} courseId={Number(courseId)} onFinished={refreshCourse}
        onSessions={(sessions) => setUnit({ ...unit, sessions })}
      />

      {unit.type !== 'test' && unit.type !== 'survey' && unit.type !== 'assignment' && unit.type !== 'ilt' && (
        <footer className="unit__footer">
          {!done && unit.completionType === 'question' && unit.completionParams?.configured && <fieldset>
            <legend>{unit.completionParams.prompt}</legend>
            {(unit.completionParams.options as string[]).map((option, i) => <label className="row" key={i}>
              <input type={unit.completionParams!.multipleAnswers ? 'checkbox' : 'radio'} name="completion-question" checked={completionChoice.includes(i)} onChange={e => setCompletionChoice(unit.completionParams!.multipleAnswers ? (e.target.checked ? [...completionChoice, i] : completionChoice.filter(v => v !== i)) : [i])} />{option}
            </label>)}
          </fieldset>}
          {completionNotice && <p role="status">{completionNotice}</p>}
          {!done && questionCompletionUnavailable && <p role="status">{unit.completionParams?.message ?? 'This lesson requires a completion question. Please contact your instructor to configure it.'}</p>}
          {done
            ? <span className="pill pill--completed">Completed</span>
            : <button className="btn btn--primary" onClick={() => void finish()} disabled={saving || questionCompletionUnavailable}>
                {saving ? 'Saving…' : next ? 'Mark complete and continue' : 'Mark complete'}
              </button>}
          {next && <Link className="btn btn--ghost" to={`/courses/${courseId}/units/${next.id}`}>Next: {next.name}</Link>}
        </footer>
      )}
    </article>
  );
}

function UnitBody({ unit, courseId, onFinished, onSessions }: {
  unit: UnitDetail; courseId: number; onFinished: () => void;
  onSessions: (sessions: NonNullable<UnitDetail['sessions']>) => void;
}) {
  switch (unit.type) {
    case 'craft':
      return unit.craft
        ? <CraftLesson blocks={unit.craft.blocks} theme={unit.craft.theme as any} downloadDisabled={unit.downloadDisabled} />
        : <p className="muted">This lesson has no content yet.</p>;

    case 'test':
    case 'survey':
      return <TestPlayer unit={unit} onFinished={onFinished} />;

    case 'video':
      return (
        <>
          <MediaPlayer url={unit.url} kind="video" downloadDisabled={unit.downloadDisabled} />
          {unit.secondaryHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: unit.secondaryHtml }} />}
        </>
      );

    case 'audio':
      return (
        <>
          <MediaPlayer url={unit.url} kind="audio" downloadDisabled={unit.downloadDisabled} />
          {unit.secondaryHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: unit.secondaryHtml }} />}
        </>
      );

    case 'document':
      return unit.url
        ? <object className="unit__doc" data={unit.url} type="application/pdf">
            <p>Your browser cannot show this file. {!unit.downloadDisabled && <a href={unit.url}>Download it instead.</a>}</p>
          </object>
        : <p className="muted">No document attached.</p>;

    case 'webpage':
      return unit.url
        ? <iframe className="unit__frame" src={unit.url} title={unit.name} loading="lazy" />
        : <p className="muted">No page attached.</p>;

    case 'ilt':
      return (
        <ClassUnit
          courseId={courseId} unitId={unit.id}
          contentHtml={unit.contentHtml ?? null}
          sessions={unit.sessions ?? []}
          onChanged={onSessions}
        />
      );

    case 'assignment':
      return <AssignmentUnit courseId={courseId} unitId={unit.id} onFinished={onFinished} />;

    default:
      return unit.contentHtml
        ? <div className="prose" dangerouslySetInnerHTML={{ __html: unit.contentHtml }} />
        : <p className="muted">Nothing to show.</p>;
  }
}

function MediaPlayer({ url, kind, downloadDisabled }: { url: string | null; kind: 'video' | 'audio'; downloadDisabled?: boolean }) {
  if (!url) return <p className="muted">No file attached.</p>;
  if (/youtube\.com|youtu\.be|vimeo\.com/.test(url)) {
    return <iframe className="unit__frame" src={url} title="Video" allowFullScreen loading="lazy"
      allow="accelerometer; encrypted-media; picture-in-picture" />;
  }
  return kind === 'video'
    ? <video className="unit__video" src={url} controls controlsList={downloadDisabled ? 'nodownload' : undefined} preload="metadata" />
    : <audio src={url} controls controlsList={downloadDisabled ? 'nodownload' : undefined} preload="metadata" />;
}
